import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import { resolveLeadDisplayName } from "@/lib/lead-display-name";
import type { LeadTag } from "@/types/lead";
import type { SupabaseClient } from "@supabase/supabase-js";
import { isHumanHandoffVisualStage } from "@/lib/lead-stage-agent-handoff";

export const CONVERSATION_LIST_SELECT = `
  id,
  channel,
  external_user_id,
  state,
  followup_enabled,
  updated_at,
  version,
  is_closed,
  last_message_preview,
  last_message_at,
  last_message_direction,
  unread_count,
  escalated_by_agent_at,
  integration_id,
  integration:integration_id (
    id,
    provider,
    instance_identifier,
    config
  ),
  lead:lead_id (
    id,
    name,
    phone,
    metadata,
    stage,
    funnel_id,
    assigned_member_id,
    current_appointment_date,
    lead_tag_assignments (
      tag:tag_id ( id, name, color )
    )
  )
` as const;

export type MappedConversation = {
  conversation_id: string;
  integration_id?: string | null;
  channel: string;
  lead_id: string;
  lead_name: string;
  lead_phone: string | null;
  state: string;
  updated_at: string;
  version?: number;
  followup_enabled: boolean;
  metadata: Record<string, unknown>;
  isUnread?: boolean;
  isUrgent?: boolean;
  unreadCount?: number;
  lastMessageBody?: string | null;
  lastMessageFrom?: string | null;
  lastMessageAt?: string | null;
  is_closed?: boolean;
  escalated_by_agent_at?: string | null;
  originLabel?: string | null;
  tagIds?: string[];
  leadTags?: LeadTag[];
  assigned_member_id?: string | null;
  assigned_member_ids?: string[];
  assignedMemberLabel?: string | null;
  lead_stage?: string;
  lead_funnel_id?: string | null;
  stage_auto_disables_agent?: boolean;
  hasUpcomingAppointment?: boolean;
  hasLastCancelledAppointment?: boolean;
};

export function effectiveConversationUnreadCount(
  conversationId: string,
  unreadCount: number,
  lastMessageAt: string | null | undefined,
  readTimestamps: Map<string, string>,
): number {
  const readAt = readTimestamps.get(conversationId);
  if (readAt && lastMessageAt && lastMessageAt <= readAt) {
    return 0;
  }
  return unreadCount;
}

export function loadConversationReadTimestamps(workspaceId: string): Map<string, string> {
  try {
    const saved = localStorage.getItem(`conversation-read-timestamps-${workspaceId}`);
    if (!saved) return new Map();
    return new Map(Object.entries(JSON.parse(saved) as Record<string, string>));
  } catch {
    return new Map();
  }
}

function resolveMemberDisplayLabel(
  displayName: string | null | undefined,
  fullName: string | null | undefined,
): string | null {
  const name = (displayName || fullName || "").trim();
  return name || null;
}

export function mapRawConversationRow(
  conv: Record<string, unknown>,
  readTimestamps: Map<string, string>,
): MappedConversation | null {
  const lead = conv.lead as Record<string, unknown> | null | undefined;
  if (!lead) return null;

  const leadMetadata = (lead.metadata as Record<string, unknown> | null) ?? {};
  const externalUserId = String(conv.external_user_id ?? "");
  const channel = String(conv.channel ?? "");

  const displayName = resolveLeadDisplayName({
    name: lead.name as string | null,
    phone: lead.phone as string | null,
    metadata: leadMetadata,
    channel,
    externalUserId,
  });

  const displayPhone =
    channel === "instagram"
      ? leadMetadata.instagram_username
        ? `@${leadMetadata.instagram_username}`
        : externalUserId
          ? `ID: ${externalUserId.slice(-6)}`
          : null
      : (lead.phone as string | null) ?? null;

  const integrationRaw = conv.integration;
  const integration = Array.isArray(integrationRaw)
    ? (integrationRaw[0] as Record<string, unknown> | undefined)
    : (integrationRaw as Record<string, unknown> | undefined);

  const integrationConfig = (integration?.config as Record<string, unknown> | undefined) ?? {};
  const originLabel = integration
    ? channel === "whatsapp"
      ? whatsappIntegrationDisplayLabel(integration)
      : channel === "instagram"
        ? integrationConfig.username
          ? `@${integrationConfig.username}`
          : String(integrationConfig.name || "Instagram")
        : String(integrationConfig.page_name || "Messenger")
    : "Sem conexão";

  const convId = String(conv.id);
  const lastMessageAt = conv.last_message_at as string | null | undefined;
  const unreadCount = effectiveConversationUnreadCount(
    convId,
    Number(conv.unread_count) || 0,
    lastMessageAt,
    readTimestamps,
  );

  let previewBody = conv.last_message_preview as string | null | undefined;
  if (previewBody === "[Vídeo]" || previewBody === "[Video]") previewBody = "🎥 Vídeo";
  else if (previewBody === "[Imagem]" || previewBody === "[imagem]") previewBody = "📷 Imagem";

  const tagAssignments = lead.lead_tag_assignments as
    | Array<{ tag_id?: string; tag?: LeadTag | LeadTag[] | null }>
    | undefined;
  const leadTags: LeadTag[] = [];
  const tagIds: string[] = [];
  if (Array.isArray(tagAssignments)) {
    for (const assignment of tagAssignments) {
      const tagRaw = assignment.tag;
      const tag = Array.isArray(tagRaw) ? tagRaw[0] : tagRaw;
      if (tag?.id) {
        leadTags.push({ id: tag.id, name: tag.name, color: tag.color });
        tagIds.push(tag.id);
      } else if (assignment.tag_id) {
        tagIds.push(assignment.tag_id);
      }
    }
  }
  const appointmentDate = lead.current_appointment_date as string | null | undefined;
  const hasUpcomingAppointment = Boolean(
    appointmentDate && new Date(appointmentDate) > new Date(),
  );

  return {
    conversation_id: convId,
    integration_id: (conv.integration_id as string | null) ?? null,
    channel,
    lead_id: String(lead.id),
    lead_name: displayName,
    lead_phone: displayPhone,
    state: String(conv.state ?? ""),
    updated_at: String(conv.updated_at ?? ""),
    version: typeof conv.version === "number" ? conv.version : undefined,
    followup_enabled: Boolean(conv.followup_enabled),
    metadata: {
      ...leadMetadata,
      profile_picture: leadMetadata.profile_picture,
      instagram_profile_pic: leadMetadata.instagram_profile_pic,
      instagram_username: leadMetadata.instagram_username,
    },
    isUnread: unreadCount > 0,
    unreadCount,
    isUrgent: false,
    lead_stage: String(lead.stage ?? ""),
    lead_funnel_id: (lead.funnel_id as string | null) ?? null,
    lastMessageBody: previewBody || null,
    lastMessageFrom:
      conv.last_message_direction === "outbound" ? "assistant" : externalUserId,
    lastMessageAt: lastMessageAt || String(conv.updated_at ?? ""),
    assigned_member_id: (lead.assigned_member_id as string | null) ?? null,
    is_closed: Boolean(conv.is_closed),
    escalated_by_agent_at: (conv.escalated_by_agent_at as string | null) ?? null,
    originLabel,
    tagIds,
    leadTags,
    hasUpcomingAppointment,
  };
}

type AppointmentIndicatorRow = {
  lead_id: string | null;
  status: string;
  cancellation_type: string | null;
  created_at: string;
};

function isCancelledWithoutReschedule(appt: AppointmentIndicatorRow): boolean {
  return appt.status === "cancelled" && appt.cancellation_type !== "rescheduled";
}

export async function enrichConversationsWithAppointmentIndicators(
  client: SupabaseClient,
  conversations: MappedConversation[],
  workspaceId?: string,
): Promise<MappedConversation[]> {
  const leadIdsToCheck = [
    ...new Set(
      conversations.filter((conv) => !conv.hasUpcomingAppointment).map((conv) => conv.lead_id),
    ),
  ];

  if (leadIdsToCheck.length === 0) {
    return conversations;
  }

  let query = client
    .from("appointments")
    .select("lead_id, status, cancellation_type, created_at")
    .in("lead_id", leadIdsToCheck)
    .neq("sync_status", "suppressed");

  if (workspaceId) {
    query = query.eq("workspace_id", workspaceId);
  }

  const { data, error } = await query
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching appointment indicators:", error);
    return conversations;
  }

  const lastByLead = new Map<string, AppointmentIndicatorRow>();
  for (const row of (data ?? []) as AppointmentIndicatorRow[]) {
    if (!row.lead_id || lastByLead.has(row.lead_id)) continue;
    lastByLead.set(row.lead_id, row);
  }

  const cancelledLeadIds = new Set<string>();
  for (const [leadId, appt] of lastByLead) {
    if (isCancelledWithoutReschedule(appt)) {
      cancelledLeadIds.add(leadId);
    }
  }

  return conversations.map((conv) => ({
    ...conv,
    hasLastCancelledAppointment:
      !conv.hasUpcomingAppointment && cancelledLeadIds.has(conv.lead_id),
  }));
}

/** Resolve responsáveis do lead → rótulos (batch, evita N+1 na lista). */
export async function enrichConversationsWithAssignedMemberLabels(
  client: SupabaseClient,
  conversations: MappedConversation[],
): Promise<MappedConversation[]> {
  const leadIds = [...new Set(conversations.map((c) => c.lead_id).filter(Boolean))];
  if (leadIds.length === 0) return conversations;

  const { data: assignmentRows, error: assignmentError } = await client
    .from("lead_member_assignments")
    .select("lead_id, member_id")
    .in("lead_id", leadIds);

  if (assignmentError) {
    console.error("Error fetching lead assignees for inbox:", assignmentError);
  }

  const assigneesByLeadId = new Map<string, string[]>();
  for (const row of assignmentRows ?? []) {
    const list = assigneesByLeadId.get(row.lead_id) ?? [];
    list.push(row.member_id);
    assigneesByLeadId.set(row.lead_id, list);
  }

  for (const conv of conversations) {
    if (!assigneesByLeadId.has(conv.lead_id) && conv.assigned_member_id) {
      assigneesByLeadId.set(conv.lead_id, [conv.assigned_member_id]);
    }
  }

  const memberIds = [
    ...new Set(
      [...assigneesByLeadId.values()].flat(),
    ),
  ];

  if (memberIds.length === 0) {
    return conversations.map((conv) => ({
      ...conv,
      assigned_member_ids: assigneesByLeadId.get(conv.lead_id) ?? [],
    }));
  }

  const { data: members, error: membersError } = await client
    .from("workspace_members")
    .select("id, display_name, user_id")
    .in("id", memberIds);

  if (membersError) {
    console.error("Error fetching assigned members for inbox:", membersError);
    return conversations;
  }

  const userIds = (members ?? []).map((m) => m.user_id).filter(Boolean);
  const { data: profiles } =
    userIds.length > 0
      ? await client.from("profiles").select("id, full_name").in("id", userIds)
      : { data: [] as { id: string; full_name: string | null }[] };

  const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));
  const labelByMemberId = new Map(
    (members ?? []).map((m) => [
      m.id,
      resolveMemberDisplayLabel(m.display_name, profileMap.get(m.user_id)),
    ]),
  );

  return conversations.map((conv) => {
    const assignedIds = assigneesByLeadId.get(conv.lead_id) ?? [];
    const labels = assignedIds
      .map((id) => labelByMemberId.get(id))
      .filter((label): label is string => Boolean(label));
    return {
      ...conv,
      assigned_member_id: assignedIds[0] ?? conv.assigned_member_id ?? null,
      assigned_member_ids: assignedIds,
      assignedMemberLabel: labels.length > 0 ? labels.join(", ") : null,
    };
  });
}

export async function enrichConversationsWithStageHandoffFlags(
  client: SupabaseClient,
  workspaceId: string,
  conversations: MappedConversation[],
): Promise<MappedConversation[]> {
  const pairs = [
    ...new Map(
      conversations.map((c) => [
        `${c.lead_funnel_id ?? "null"}:${c.lead_stage ?? ""}`,
        { funnel_id: c.lead_funnel_id ?? null, slug: c.lead_stage ?? "" },
      ]),
    ).values(),
  ].filter((p) => p.slug);

  if (pairs.length === 0) {
    return conversations.map((c) => ({
      ...c,
      stage_auto_disables_agent: c.lead_stage === "atendimento_humano",
      isUrgent: isHumanHandoffVisualStage(c.lead_stage),
    }));
  }

  const slugs = [...new Set(pairs.map((p) => p.slug))];

  const { data, error } = await client
    .from("lead_stages")
    .select("slug, funnel_id, auto_disable_agent")
    .eq("workspace_id", workspaceId)
    .in("slug", slugs);

  if (error) {
    console.error("enrichConversationsWithStageHandoffFlags:", error);
    return conversations.map((c) => ({
      ...c,
      stage_auto_disables_agent: c.lead_stage === "atendimento_humano",
      isUrgent: isHumanHandoffVisualStage(c.lead_stage),
    }));
  }

  const flagMap = new Map<string, boolean>();
  for (const row of data ?? []) {
    const key = `${row.funnel_id ?? "null"}:${row.slug}`;
    flagMap.set(key, row.auto_disable_agent === true);
  }

  return conversations.map((c) => {
    const key = `${c.lead_funnel_id ?? "null"}:${c.lead_stage ?? ""}`;
    const disablesAgent = flagMap.get(key) ?? c.lead_stage === "atendimento_humano";
    return {
      ...c,
      stage_auto_disables_agent: disablesAgent,
      isUrgent: isHumanHandoffVisualStage(c.lead_stage),
    };
  });
}

export async function enrichConversationsList(
  client: SupabaseClient,
  conversations: MappedConversation[],
  workspaceId?: string,
): Promise<MappedConversation[]> {
  const withAppointments = await enrichConversationsWithAppointmentIndicators(
    client,
    conversations,
    workspaceId,
  );
  const withMembers = await enrichConversationsWithAssignedMemberLabels(client, withAppointments);
  if (!workspaceId) return withMembers;
  return enrichConversationsWithStageHandoffFlags(client, workspaceId, withMembers);
}

export function sortConversationsByIdOrder<T extends { conversation_id: string }>(
  items: T[],
  orderedIds: string[],
): T[] {
  const orderMap = new Map(orderedIds.map((id, index) => [id, index]));
  return [...items].sort(
    (a, b) =>
      (orderMap.get(a.conversation_id) ?? 9999) - (orderMap.get(b.conversation_id) ?? 9999),
  );
}
